/**
 * ChromaLabel cart "What will you use it for?" question (2026-10-02)
 *
 * Saves the optional answer from snippets/cl-use-case.liquid into the cart
 * attributes `use_case` and `use_case_detail`. Shopify copies cart attributes
 * onto the order as note_attributes.
 *
 * Rules:
 *  - Only the two keys above are ever sent to /cart/update.js. Shopify MERGES
 *    attributes on update, so every other attribute (first_touch_*, visitor_id,
 *    shopify_y, ... from snippets/first-touch-tracker.liquid) is preserved.
 *    Re-sending the whole attribute set would risk overwriting a concurrent
 *    first-touch write with stale values, so we deliberately do not.
 *  - /cart.js is read on load so the fields reflect the saved answer even when
 *    the drawer markup is stale.
 *  - Never blocks checkout: no preventDefault anywhere. On checkout we flush
 *    any pending save with fetch keepalive; the fields also carry
 *    name="attributes[...]" so the native form submit saves them too.
 *  - Works with the cart page and the mini-cart drawer. Both are re-rendered by
 *    the theme (innerHTML swap), so listeners are delegated on document and a
 *    MutationObserver re-applies any unsaved answer to fresh markup.
 */
(function () {
  'use strict';

  if (window.__clUseCase) return;
  window.__clUseCase = true;

  var KEYS = ['use_case', 'use_case_detail'];
  var MAX_DETAIL = 200;
  var DEBOUNCE_MS = 700;
  var cartUrl = (window.routes && window.routes.cartUrl) || '/cart';

  /** Latest known answer (what the shopper has entered), or null if unknown. */
  var answer = null;
  /** Keys changed since the last successful save. */
  var dirty = {};
  var timer = null;
  var queue = Promise.resolve();

  /**
   * Normalises a value before it is stored.
   * @param {string} key - attribute key
   * @param {string} value - raw field value
   * @returns {string} trimmed (and length-capped) value
   */
  function clean(key, value) {
    var v = String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
    return key === 'use_case_detail' ? v.slice(0, MAX_DETAIL) : v;
  }

  /** @returns {NodeList} every rendered question block on the page */
  function blocks() {
    return document.querySelectorAll('[data-cl-use-case]');
  }

  /**
   * Shows a short status message in every block.
   * @param {string} text - message ('' hides it)
   */
  function status(text) {
    var nodes = document.querySelectorAll('[data-cl-use-case-status]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = text;
      nodes[i].classList.toggle('is-visible', !!text);
    }
  }

  /**
   * Writes the given answer into one block's fields (skipping a focused field
   * so we never fight the shopper's typing).
   * @param {Element} block - [data-cl-use-case] element
   * @param {Object} vals - {use_case, use_case_detail}
   */
  function fill(block, vals) {
    KEYS.forEach(function (key) {
      if (!(key in vals)) return;
      var field = block.querySelector('[data-cl-use-case-field="' + key + '"]');
      if (!field || field === document.activeElement) return;
      var v = vals[key] || '';
      if (field.tagName === 'SELECT' && v && !field.querySelector('option[value="' + cssEscape(v) + '"]')) {
        var opt = document.createElement('option');
        opt.value = v;
        opt.textContent = v;
        field.appendChild(opt);
      }
      if (field.value !== v) field.value = v;
    });
  }

  /** Minimal CSS.escape fallback for attribute selectors. */
  function cssEscape(v) {
    return window.CSS && CSS.escape ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
  }

  /** Applies the in-memory answer to every block (e.g. after a re-render). */
  function fillAll() {
    if (!answer) return;
    var list = blocks();
    for (var i = 0; i < list.length; i++) fill(list[i], answer);
  }

  /**
   * Sends the dirty keys to /cart/update.js. Requests are serialised so an
   * older save can never land after a newer one.
   * @param {boolean} keepalive - true when the page may be unloading (checkout)
   * @returns {Promise}
   */
  function save(keepalive) {
    clearTimeout(timer);
    timer = null;
    var keys = Object.keys(dirty);
    if (!keys.length) return queue;
    var attributes = {};
    keys.forEach(function (k) { attributes[k] = dirty[k]; }); // '' removes the key in Shopify
    dirty = {};

    queue = queue.then(function () {
      return fetch(cartUrl + '/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: !!keepalive,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (cart) {
          var saved = cart.attributes || {};
          status(saved.use_case || saved.use_case_detail ? 'Thanks — saved.' : '');
          document.dispatchEvent(new CustomEvent('cl:use-case:saved', { detail: { attributes: saved } }));
        })
        .catch(function () {
          // Put the keys back so the next change/checkout retries. Never surface
          // an error that could make a shopper think checkout is blocked.
          keys.forEach(function (k) { if (!(k in dirty)) dirty[k] = attributes[k]; });
        });
    });
    return queue;
  }

  /**
   * Records a field change and syncs the value into any other block.
   * @param {Element} field - the changed select/input
   * @param {boolean} immediate - save now instead of debouncing
   */
  function onFieldChange(field, immediate) {
    var key = field.getAttribute('data-cl-use-case-field');
    if (KEYS.indexOf(key) === -1) return;
    var value = clean(key, field.value);
    answer = answer || {};
    if (answer[key] === value && !(key in dirty)) return;
    answer[key] = value;
    dirty[key] = value;

    var list = blocks();
    for (var i = 0; i < list.length; i++) {
      if (!list[i].contains(field)) {
        var single = {};
        single[key] = value;
        fill(list[i], single);
      }
    }

    clearTimeout(timer);
    if (immediate) save(false);
    else timer = setTimeout(function () { save(false); }, DEBOUNCE_MS);
  }

  document.addEventListener('change', function (e) {
    var f = e.target;
    if (f && f.closest && f.closest('[data-cl-use-case]')) onFieldChange(f, f.tagName === 'SELECT');
  });

  document.addEventListener('input', function (e) {
    var f = e.target;
    if (f && f.tagName === 'INPUT' && f.closest && f.closest('[data-cl-use-case]')) onFieldChange(f, false);
  });

  // Enter in the text box should save, not submit the cart form (which would
  // send the shopper to /cart or checkout unexpectedly).
  document.addEventListener('keydown', function (e) {
    var f = e.target;
    if (e.key === 'Enter' && f && f.tagName === 'INPUT' && f.closest && f.closest('[data-cl-use-case]')) {
      e.preventDefault();
      onFieldChange(f, true);
    }
  });

  // Flush a pending save when the shopper heads to checkout. Never blocks.
  function flushForCheckout(e) {
    var t = e.target;
    var isCheckout =
      (e.type === 'click' && t && t.closest && t.closest('[name="checkout"], a[href*="/checkout"]')) ||
      (e.type === 'submit' && t && t.querySelector && t.querySelector('[data-cl-use-case]'));
    if (isCheckout && Object.keys(dirty).length) save(true);
  }
  document.addEventListener('click', flushForCheckout, true);
  document.addEventListener('submit', flushForCheckout, true);
  window.addEventListener('pagehide', function () {
    if (Object.keys(dirty).length) save(true);
  });

  // Theme re-renders the drawer / cart section by swapping innerHTML. Re-apply
  // any answer that has not reached the server yet to the fresh markup.
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.matches('[data-cl-use-case]') || n.querySelector('[data-cl-use-case]'))) {
            fillAll();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Hydrate from the live cart so stale markup never shows an old answer.
  fetch(cartUrl + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (cart) {
      if (!cart) return;
      var attrs = cart.attributes || {};
      var fromServer = {};
      KEYS.forEach(function (k) {
        // A key edited before this response arrived wins over the server copy.
        fromServer[k] = answer && k in answer ? answer[k] : attrs[k] || '';
      });
      answer = fromServer;
      fillAll();
    })
    .catch(function () {});
})();
